import { useCallback, useState } from "react";
import { KeyRound, Mail } from "lucide-react";

import { AuthService } from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useLocale, useTranslation } from "@/i18n";
import type { Locale, TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/shared/section";

// Role identifiers map to translation keys so the role badge
// re-renders on a locale switch.
const ROLE_LABEL_KEY: Record<string, TranslationKey> = {
  super_admin: "settings.roles.super_admin",
  admin: "settings.roles.admin",
  member: "settings.roles.member",
  viewer: "settings.roles.viewer",
  partner_admin: "settings.roles.partner_admin",
};

export default function SettingsProfileTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [resetSent, setResetSent] = useState(false);

  const mutator = useCallback(
    (email: string) => AuthService.forgotPassword({ email }),
    []
  );
  const reset = useMutation<string, void>(mutator, {
    onSuccess: () => setResetSent(true),
  });

  const initials = (user?.full_name ?? user?.email ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

  const emptyValue = t("settings.profile.identity.emptyValue");
  const roleLabel =
    user && ROLE_LABEL_KEY[user.role]
      ? t(ROLE_LABEL_KEY[user.role])
      : user?.role ?? emptyValue;

  return (
    <div className="space-y-8">
      <Section
        title={t("settings.profile.identity.title")}
        description={t("settings.profile.identity.description")}
      >
        <div className="rounded-lg border border-ocean-100 bg-white p-5">
          <div className="flex items-start gap-4">
            <div
              aria-hidden="true"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-ocean-100 text-base font-medium text-ocean-800"
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-base font-medium text-ocean-900">
                {user?.full_name || emptyValue}
              </div>
              <div className="mt-0.5 text-sm text-neutral-600">
                {user?.email || emptyValue}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 tablet:grid-cols-2">
                <Field label={t("settings.profile.identity.roleLabel")}>
                  <Badge tone="ocean">{user ? roleLabel : emptyValue}</Badge>
                </Field>
                <Field label={t("settings.profile.identity.tenantIdLabel")}>
                  <code className="break-all text-xs text-neutral-700">
                    {user?.tenant_id ?? emptyValue}
                  </code>
                </Field>
                {user?.workspace_id && (
                  <Field
                    label={t("settings.profile.identity.workspaceLabel")}
                  >
                    <code className="break-all text-xs text-neutral-700">
                      {user.workspace_id}
                    </code>
                  </Field>
                )}
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section
        title={t("settings.profile.password.title")}
        description={t("settings.profile.password.description")}
      >
        <div className="rounded-lg border border-ocean-100 bg-white p-5">
          {reset.error && (
            <Alert variant="danger" className="mb-3">
              {parseApiError(reset.error)}
            </Alert>
          )}
          {resetSent && (
            <Alert variant="success" className="mb-3" role="status">
              {t("settings.profile.password.sentNotice", {
                email: user?.email ?? "",
              })}
            </Alert>
          )}
          <p className="text-sm text-neutral-600">
            {t("settings.profile.password.intro")}
          </p>
          <div className="mt-4 flex items-center gap-2">
            <Button
              onClick={() => {
                if (user?.email) void reset.mutate(user.email);
              }}
              disabled={reset.isLoading || !user?.email}
            >
              <Mail size={14} />
              {reset.isLoading
                ? t("settings.profile.password.sendButtonLoading")
                : t("settings.profile.password.sendButton")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResetSent(false)}
              disabled={!resetSent || reset.isLoading}
              aria-label={t("settings.profile.password.resendAria")}
            >
              <KeyRound size={14} />
              {t("settings.profile.password.resendButton")}
            </Button>
          </div>
        </div>
      </Section>

      <LanguageSection />
    </div>
  );
}

/**
 * Language preference section. The radio group is wired directly
 * to the shared LocaleProvider so this control and the user-menu
 * locale switcher are a single source of truth (both persist to
 * ``monago.locale``).
 */
function LanguageSection() {
  const { locale, setLocale, t } = useLocale();

  return (
    <Section
      title={t("settings.profile.language.title")}
      description={t("settings.profile.language.description")}
    >
      <div className="rounded-lg border border-ocean-100 bg-white p-5">
        <div
          role="radiogroup"
          aria-label={t("settings.profile.language.groupAria")}
          className="flex flex-col gap-2"
        >
          <LanguageOption
            value="id"
            label={t("settings.profile.language.idLabel")}
            description={t("settings.profile.language.idDescription")}
            checked={locale === "id"}
            onChange={() => setLocale("id")}
          />
          <LanguageOption
            value="en"
            label={t("settings.profile.language.enLabel")}
            description={t("settings.profile.language.enDescription")}
            checked={locale === "en"}
            onChange={() => setLocale("en")}
          />
        </div>
      </div>
    </Section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function LanguageOption({
  value,
  label,
  description,
  checked,
  onChange,
}: {
  value: Locale;
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 transition-colors",
        checked
          ? "border-ocean-300 bg-ocean-50"
          : "border-ocean-100 hover:border-ocean-200"
      )}
    >
      <input
        type="radio"
        name="profile-language"
        value={value}
        checked={checked}
        onChange={onChange}
        className="mt-1 h-4 w-4 border-ocean-200 text-ocean-600 focus:ring-ocean-400"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium text-ocean-900">{label}</div>
        <div className="mt-0.5 text-xs text-neutral-600">{description}</div>
      </div>
    </label>
  );
}
